import { CasinoPromoConfig, ProductCategoryEnum } from "@/widgets/casinoWidget/casino.types";
import { normalizeBadgeLobbyResponse } from "./badge.mapper";
import { BadgesApiResponse, BadgeQuest } from "./types";

type CasinoBadgeModel = CasinoPromoConfig & { badgeData: BadgesApiResponse; exRates: any[] };

const getBadgeDateRange = (
  badgeQuests: BadgeQuest[],
  fallbackStartDate?: string | null,
  fallbackEndDate?: string | null
) => {
  const now = new Date().toISOString();

  if (badgeQuests.length === 0) {
    return {
      startDate: fallbackStartDate || now,
      endDate: fallbackEndDate || fallbackStartDate || now,
    };
  }

  let minStartDate: string | null = null;
  let maxEndDate: string | null = null;

  for (const quest of badgeQuests) {
    if (quest?.startDate) {
      if (!minStartDate || new Date(quest.startDate).getTime() < new Date(minStartDate).getTime()) {
        minStartDate = quest.startDate;
      }
    }

    if (quest?.endDate) {
      if (!maxEndDate || new Date(quest.endDate).getTime() > new Date(maxEndDate).getTime()) {
        maxEndDate = quest.endDate;
      }
    }
  }

  return {
    startDate: minStartDate || fallbackStartDate || now,
    endDate: maxEndDate || fallbackEndDate || fallbackStartDate || minStartDate || now,
  };
};

const getSortedBadgeQuests = (normalizedBadgeData: BadgesApiResponse): BadgeQuest[] => {
  return (normalizedBadgeData?.badgeGroups || [])
    .flatMap((group) => group.badges || [])
    .map((badge) => badge.quest)
    .filter((quest): quest is BadgeQuest => Boolean(quest))
    .sort((a, b) => {
      const orderingDiff =
        Number((a as any)?.badge?.orderingScore ?? 0) - Number((b as any)?.badge?.orderingScore ?? 0);
      if (orderingDiff !== 0) return orderingDiff;
      return (a?.stepId ?? 0) - (b?.stepId ?? 0);
    });
};

export const toCasinoBadgePromoConfig = (badgesPayload: unknown): CasinoBadgeModel => {
  const normalizedBadgeData = normalizeBadgeLobbyResponse(badgesPayload);
  const rawPayload = badgesPayload as any;
  const badgeQuests = getSortedBadgeQuests(normalizedBadgeData);
  const { startDate, endDate } = getBadgeDateRange(
    badgeQuests,
    rawPayload?.startDate,
    rawPayload?.endDate
  );

  return {
    countdown: !!rawPayload?.countdown,
    id: Number(rawPayload?.id || 0),
    name: rawPayload?.name || "Badge Endless",
    slug: rawPayload?.slug ?? null,
    promoRef: normalizedBadgeData?.promoRef || rawPayload?.promoRef || "",
    categoryId: Number(rawPayload?.categoryId || 0),
    teaserDays: Number(rawPayload?.teaserDays || 0),
    startDate,
    endDate,
    status: rawPayload?.status || "ACTIVE",
    periodType: rawPayload?.periodType ?? null,
    periodValue: rawPayload?.periodValue || "",
    participants: Number(rawPayload?.participants || 0),
    optIn: !!rawPayload?.optIn,
    campaignAccess: rawPayload?.campaignAccess || "PUBLIC",
    restrictedSegments: rawPayload?.restrictedSegments || [],
    segments: rawPayload?.segments || [],
    category: ProductCategoryEnum.BadgeEndless,
    questsUnlockStrategy: rawPayload?.questsUnlockStrategy || "PARALLEL",
    quest: badgeQuests,
    questsSet: rawPayload?.questsSet || [],
    infinite: rawPayload?.infinite ?? true,
    timezoneOffset: Number(rawPayload?.timezoneOffset || 0),
    randomizerMilestones: rawPayload?.randomizerMilestones || [],
    casinoReward: rawPayload?.casinoReward || [],
    hasLeaderboard: !!rawPayload?.hasLeaderboard,
    exRates: normalizedBadgeData?.exRates || [],
    badgeData: normalizedBadgeData,
  };
};
